// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/* istanbul ignore file */
import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { HIST_VALUES } from '@shared/ui-kit/components/period-picker';
import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import {
  selectComparisonCorrMatrix,
  selectComparisonBenchmark,
  selectComparisonHist,
  selectComparisonTable,
  selectComparisonTickers,
  selectIdeas,
  setComparisonBenchmark,
  setComparisonHist,
  selectComparisonRisk,
  selectComparisonBacktest,
  selectComparisonHistoricalData,
  selectComparisonAssetsInfo,
  setComparisonTickers,
  removeComparisonTickers,
} from '@advisor-app/store/slices/my-research';
import { selectAllTags } from '@shared/data-access/slices/tag-manager';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { useTranslation } from 'react-i18next';

export const useComparison = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const w_hist = useAppSelector(selectComparisonHist);

  const benchmark = useAppSelector(selectComparisonBenchmark);

  const tickers = useAppSelector(selectComparisonTickers);

  const assetsTable = useAppSelector(selectComparisonTable);

  const correlationMatrixData = useAppSelector(selectComparisonCorrMatrix);

  const risksData = useAppSelector(selectComparisonRisk);

  const backtestData = useAppSelector(selectComparisonBacktest);

  const histValue = useMemo(() => HIST_VALUES.get(w_hist), [w_hist]);

  const portfolios = useAppSelector(selectIdeas);

  const watchList = useAppSelector(state =>
    selectAllTags(state, TickerTagFeature.SelectedAssets),
  );

  const aggregator = useAppSelector(state =>
    selectAllTags(state, TickerTagFeature.AggregatorPositions),
  );

  const assetsInfo = useAppSelector(selectComparisonAssetsInfo);

  const { isLoading, isFetching } = assetsApi.useComparisonQuery({
    tickers,
    benchmark,
    hist_w: w_hist,
  });

  const assetsMap = useMemo(
    () => new Map(assetsInfo.map(({ ticker, name }) => [ticker, name])),
    [assetsInfo],
  );

  const historicalData = useAppSelector(selectComparisonHistoricalData);

  const correlationMatrix = useMemo(() => {
    return {
      ...correlationMatrixData,
      name: correlationMatrixData.ticker.map(
        ticker => assetsMap.get(ticker) || ticker,
      ),
    };
  }, [assetsMap, correlationMatrixData]);

  const backtest = useMemo(
    () =>
      backtestData.map(asset => ({
        ...asset,
        name: assetsMap.get(asset.ticker) || asset.ticker,
      })),
    [assetsMap, backtestData],
  );

  const risks = useMemo(
    () =>
      risksData.map(risk => ({
        ...risk,
        name: assetsMap.get(risk.ticker) || risk.ticker,
      })),
    [assetsMap, risksData],
  );

  const portfolioOptions = useMemo(
    () => [
      ...portfolios.map(({ portfolio, portfolio_id, holdings }) => ({
        id: portfolio_id,
        name: portfolio,
        tickers: holdings.map(({ ticker }) => ticker),
        type: 'Portfolio',
      })),
      ...watchList.map(({ name, tickers }) => ({
        id: name,
        name,
        tickers: tickers,
        type: 'Selected Assets',
      })),
      ...aggregator.map(({ name, tickers }) => ({
        id: name,
        name,
        tickers: tickers,
        type: 'Aggregator',
      })),
    ],
    [aggregator, portfolios, watchList],
  );

  const [ticker, setTicker] = useState('');

  const setHist = useCallback(
    (value: number) => dispatch(setComparisonHist(value)),
    [dispatch],
  );

  const setBenchmark = useCallback(
    (value: string) => dispatch(setComparisonBenchmark(value)),
    [dispatch],
  );

  const onSelectPortfolio = useCallback(
    ({ tickers: optionsTickers }: (typeof portfolioOptions)[number]) => {
      const portfolioTickers = Array.from(new Set(tickers.concat(optionsTickers)));

      dispatch(setComparisonTickers(portfolioTickers));
    },
    [dispatch, tickers],
  );

  const onSelectTicker = useCallback(
    (ticker: string) => {
      if (tickers.includes(ticker)) return;

      setTicker('');

      dispatch(setComparisonTickers([ticker]));
    },
    [dispatch, tickers],
  );

  const onRemoveTicker = useCallback(
    (ticker: string) => {
      showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).CONFIRM_DELETE,
        icon: <AppIcon icon="ExclamationMark" fill="#1072BA" />,
        title: t('common.delete-position', { ns: 'common' }),
        content: t('common.delete-position-confirmation', { ns: 'common' }),
        preConfirm: () => {
          return dispatch(removeComparisonTickers([ticker]));
        },
      });
    },
    [dispatch, t],
  );

  const onDeletePositions = useCallback(() => {
    showModal({
      ...MODAL_TEMPLATES_OPTIONS(t).CONFIRM_DELETE,
      icon: <AppIcon icon="ExclamationMark" fill="#1072BA" />,
      title: t('portfolio.delete-positions', { ns: 'components' }),
      content: t('portfolio.delete-positions-confirmation', { ns: 'components' }),
      buttons: {
        cancel: {
          show: true,
          text: t('common.no-cancel', { ns: 'common' }),
        },
        confirm: {
          show: true,
          text: t('common.yes-delete', { ns: 'common' }),
        },
      },
      preConfirm: () => {
        return dispatch(removeComparisonTickers(tickers));
      },
    });
  }, [dispatch, tickers, t]);

  return {
    w_hist,
    setHist,
    histValue,
    portfolios,
    onSelectPortfolio,
    ticker,
    setTicker,
    onSelectTicker,
    setBenchmark,
    benchmark,
    assetsTable,
    correlationMatrix,
    risks,
    backtest,
    isLoading: isLoading || isFetching,
    onRemoveTicker,
    onDeletePositions,
    portfolioOptions,
    historicalData,
  };
};
